import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Employees from './pages/Employees';
import Products from './pages/Products';
import Sales from './pages/Sales';
import Targets from './pages/Targets';
import Reports from './pages/Reports';
import EmployeeProductSales from './pages/EmployeeProductSales';
import VolumetricCalculator from './pages/VolumetricCalculator';
import Processing from './pages/Processing';
import SalesByCountry from './pages/SalesByCountry';
import Growth from './pages/Growth';
import BranchesAdmin from './pages/BranchesAdmin';
import SuperAnalytics from './pages/SuperAnalytics';
import BranchAnalytics from './pages/BranchAnalytics';
import SuperTotals from './pages/SuperTotals';
import SuperFieldRules from './pages/SuperFieldRules';
import SuperProducts from './pages/SuperProducts';
import SuperProductCompare from './pages/SuperProductCompare';
import SuperWeekCompare from './pages/SuperWeekCompare';
import BranchWeekCompare from './pages/BranchWeekCompare';
import SuperCountries from './pages/SuperCountries';
import SuperEnterBranch from './pages/SuperEnterBranch';
import SuperTablo from './pages/SuperTablo';
import SuperMessages from './pages/SuperMessages';
import SuperAnnouncement from './pages/SuperAnnouncement';
import SuperCoupons from './pages/SuperCoupons';
import AutoRefresh from './pages/AutoRefresh';
import EmployeeMessageModal from './components/EmployeeMessageModal';
import ChangePassword from './pages/ChangePassword';
import { Toaster } from 'sonner';
import Splash from './components/Splash';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false, managerAccess = false, superOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <div className="text-lg">جاري التحميل...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Super-admin only routes (branch management, network overview)
  if (superOnly && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Strict admin/super-admin only (users, employees management)
  if (adminOnly && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Manager-level access: admin OR supervisor OR super_admin
  if (managerAccess && user.role !== 'admin' && user.role !== 'supervisor' && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <div className="text-lg">جاري التحميل...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/b/:slug" element={<Login />} />
        <Route path="/super" element={<Login />} />
        <Route path="/super/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Super-admin → /super/totals; employee → /sales (تسجيل المبيعات); others → /dashboard
  const homeRoute =
    user.role === 'super_admin' ? '/super/totals'
    : user.role === 'employee' ? '/sales'
    : '/dashboard';

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to={homeRoute} replace />} />
        <Route path="/b/:slug" element={user.role === 'super_admin' ? <SuperEnterBranch /> : <Navigate to={homeRoute} replace />} />
        <Route path="/super" element={<Navigate to={homeRoute} replace />} />
        <Route path="/super/login" element={<Navigate to={homeRoute} replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute adminOnly>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute adminOnly>
              <Employees />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute managerAccess>
              <Products />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <Sales />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-sales"
          element={
            <ProtectedRoute>
              <EmployeeProductSales />
            </ProtectedRoute>
          }
        />
        <Route
          path="/targets"
          element={
            <ProtectedRoute>
              <Targets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute managerAccess>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/volumetric"
          element={
            <ProtectedRoute>
              <VolumetricCalculator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/processing"
          element={
            <ProtectedRoute>
              <Processing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales-by-country"
          element={
            <ProtectedRoute managerAccess>
              <SalesByCountry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <ProtectedRoute managerAccess>
              <Growth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/branch-analytics"
          element={
            <ProtectedRoute managerAccess>
              <BranchAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/branch-week-compare"
          element={
            <ProtectedRoute managerAccess>
              <BranchWeekCompare />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/branches"
          element={
            <ProtectedRoute superOnly>
              <BranchesAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/analytics"
          element={
            <ProtectedRoute superOnly>
              <SuperAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/totals"
          element={
            <ProtectedRoute superOnly>
              <SuperTotals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/field-rules"
          element={
            <ProtectedRoute superOnly>
              <SuperFieldRules />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/products"
          element={
            <ProtectedRoute superOnly>
              <SuperProducts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/product-compare"
          element={
            <ProtectedRoute superOnly>
              <SuperProductCompare />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/week-compare"
          element={
            <ProtectedRoute superOnly>
              <SuperWeekCompare />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/countries"
          element={
            <ProtectedRoute superOnly>
              <SuperCountries />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/tablo/:kind"
          element={
            <ProtectedRoute superOnly>
              <SuperTablo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/messages"
          element={
            <ProtectedRoute superOnly>
              <SuperMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/announcement"
          element={
            <ProtectedRoute superOnly>
              <SuperAnnouncement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/coupons"
          element={
            <ProtectedRoute superOnly>
              <SuperCoupons />
            </ProtectedRoute>
          }
        />
        <Route
          path="/auto-refresh"
          element={
            <ProtectedRoute adminOnly>
              <AutoRefresh />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <EmployeeMessageModal />
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Splash />
        <AppRoutes />
        <Toaster position="top-center" richColors dir="rtl" closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;